import React, { Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './lib/auth';
import { ProductTypeProvider } from './lib/productType';
import { WorkspaceProvider, getSubdomain } from './lib/workspace';
import { AppShell } from './components/layout/AppShell';
import { LoginPage } from './pages/LoginPage';
import { ForcedPasswordChange } from './components/account/ChangePasswordForm';
import { ToastContainer } from './components/ui/toast';
import { PageSkeleton } from './components/ui/skeleton';
import { usePageTracking } from './hooks/usePageTracking';
import { useEngagedTime } from './hooks/useEngagedTime';
import { useFigureAttention } from './hooks/useFigureAttention';

// Lazy-loaded page components — each loads on navigation, not at startup
const CommandCentrePage = React.lazy(() => import('./pages/CommandCentrePage').then(m => ({ default: m.CommandCentrePage })));
const SupplyChainPage = React.lazy(() => import('./pages/SupplyChainPage').then(m => ({ default: m.SupplyChainPage })));
const SalesPage = React.lazy(() => import('./pages/SalesPage').then(m => ({ default: m.SalesPage })));
const ProductsPage = React.lazy(() => import('./pages/ProductsPage').then(m => ({ default: m.ProductsPage })));
const SupplierRiskPage = React.lazy(() => import('./pages/SupplierRiskPage').then(m => ({ default: m.SupplierRiskPage })));
const ReportsPage = React.lazy(() => import('./pages/ReportsPage').then(m => ({ default: m.ReportsPage })));
const RoutesPage = React.lazy(() => import('./pages/RoutesPage').then(m => ({ default: m.RoutesPage })));
const RetroBillingPage = React.lazy(() => import('./pages/RetroBillingPage').then(m => ({ default: m.RetroBillingPage })));
const AlertsPage = React.lazy(() => import('./pages/AlertsPage').then(m => ({ default: m.AlertsPage })));
const DeductionsPage = React.lazy(() => import('./pages/DeductionsPage').then(m => ({ default: m.DeductionsPage })));
const IntelligencePage = React.lazy(() => import('./pages/IntelligencePage').then(m => ({ default: m.IntelligencePage })));
const DemandPlanningPage = React.lazy(() => import('./pages/DemandPlanningPage').then(m => ({ default: m.DemandPlanningPage })));
const OosBandPage = React.lazy(() => import('./pages/OosBandPage').then(m => ({ default: m.OosBandPage })));
// SPEC-408A: /material-requirements now serves the RM-centric Material Planning Console.
const MaterialPlanningConsolePage = React.lazy(() => import('./pages/MaterialPlanningConsolePage').then(m => ({ default: m.MaterialPlanningConsolePage })));
const AnalogueMatchingPage = React.lazy(() => import('./pages/AnalogueMatchingPage').then(m => ({ default: m.AnalogueMatchingPage })));
const ProductionUploadPage = React.lazy(() => import('./pages/ProductionUploadPage').then(m => ({ default: m.ProductionUploadPage })));
const InventoryTargetsPage = React.lazy(() => import('./pages/InventoryTargetsPage').then(m => ({ default: m.InventoryTargetsPage })));
const ForecastBenchmarkPage = React.lazy(() => import('./pages/ForecastBenchmarkPage').then(m => ({ default: m.ForecastBenchmarkPage })));
// SPEC-455 — Demand Source: which demand number is live, and the legacy/book comparison.
const DemandSourcePage = React.lazy(() => import('./pages/DemandSourcePage').then(m => ({ default: m.DemandSourcePage })));
const RecommendationsPage = React.lazy(() => import('./pages/RecommendationsPage').then(m => ({ default: m.RecommendationsPage })));  // SPEC-541
// SPEC-434 — V7 promotional engine read-layer pages (DARK preview of /api/promo/*).
const BoxACommandCentrePage = React.lazy(() => import('./pages/BoxACommandCentrePage').then(m => ({ default: m.BoxACommandCentrePage })));
const ForecastWorkbenchPage = React.lazy(() => import('./pages/ForecastWorkbenchPage').then(m => ({ default: m.ForecastWorkbenchPage })));
const PromotionsPage = React.lazy(() => import('./pages/PromotionsPage').then(m => ({ default: m.PromotionsPage })));
// SPEC-411 (scaffold) — three dark-backend stub pages.
const ReplenishmentDetailPage = React.lazy(() => import('./pages/ReplenishmentDetailPage').then(m => ({ default: m.ReplenishmentDetailPage })));
const RegimeAlertsPage = React.lazy(() => import('./pages/RegimeAlertsPage').then(m => ({ default: m.RegimeAlertsPage })));
const CustomerDormancyPage = React.lazy(() => import('./pages/CustomerDormancyPage').then(m => ({ default: m.CustomerDormancyPage })));
const GovernancePage = React.lazy(() => import('./pages/GovernancePage').then(m => ({ default: m.GovernancePage })));
// SPEC-448 — /methodology renders tab components exported from GovernancePage, so the two
// pages share one lazy chunk. That is intended: nothing was copied between them.
const DocsPage = React.lazy(() => import('./pages/DocsPage').then(m => ({ default: m.DocsPage })));
const MethodologyPage = React.lazy(() => import('./pages/MethodologyPage').then(m => ({ default: m.MethodologyPage })));
const RoadmapPage = React.lazy(() => import('./pages/RoadmapPage').then(m => ({ default: m.RoadmapPage })));
const ProductDetailPage = React.lazy(() => import('./pages/ProductDetailPage').then(m => ({ default: m.ProductDetailPage })));
const AdminPage = React.lazy(() => import('./pages/AdminPage').then(m => ({ default: m.AdminPage })));
const SuperAdminPage = React.lazy(() => import('./pages/SuperAdminPage').then(m => ({ default: m.SuperAdminPage })));
const InviteRegisterPage = React.lazy(() => import('./pages/InviteRegisterPage').then(m => ({ default: m.InviteRegisterPage })));
const SSOCallbackPage = React.lazy(() => import('./pages/SSOCallbackPage').then(m => ({ default: m.SSOCallbackPage })));
// SPEC-426 — the Stats sub-application (distinct StatsShell + super_admin gate).
// Lazy so the planner bundle never pays for it; loads only on the stats surface.
const StatsApp = React.lazy(() => import('./pages/stats/StatsApp').then(m => ({ default: m.StatsApp })));

function AuthGate({ children }: { children: React.ReactNode }) {
  usePageTracking();
  useEngagedTime(); // SPEC-504 engaged-time heartbeat (visible + focused + active only)
  useFigureAttention(); // SPEC-545 figure rows on screen 2 s+ (time on screen, never "used")
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--bg-base)] flex items-center justify-center">
        <div className="text-amber-400 text-sm">Loading...</div>
      </div>
    );
  }

  if (!user) {
    return <LoginPage />;
  }

  // SPEC-489: after an admin reset, only the change-password screen is served (the server
  // answers 403 to every other route until the one-time password is replaced).
  if (user.must_change_password) {
    return <ForcedPasswordChange />;
  }

  return <>{children}</>;
}

export function App() {
  // SPEC-426: on stats.suryaiq.com the whole app IS the Stats surface (a distinct
  // StatsShell, super_admin-gated) and `/` boots into the dashboard. On every
  // other host the Stats surface is reachable at /stats* (the staging preview
  // path) while the normal planner app owns everything else.
  const onStatsHost = getSubdomain() === 'stats';

  return (
    <BrowserRouter>
      <WorkspaceProvider>
      <AuthProvider>
        <Routes>
          {/* Public routes — outside AuthGate */}
          <Route path="/sso/callback" element={
            <Suspense fallback={<PageSkeleton />}>
              <SSOCallbackPage />
            </Suspense>
          } />
          {onStatsHost ? (
            /* Stats subdomain: the entire authenticated surface is the Stats app. */
            <Route path="*" element={
              <AuthGate>
                <Suspense fallback={<PageSkeleton />}>
                  <StatsApp base="" />
                </Suspense>
              </AuthGate>
            } />
          ) : (
          <>
          {/* SPEC-566: new links are /invite#<token>; /invite/:token keeps older links working. */}
          <Route path="/invite" element={
            <Suspense fallback={<PageSkeleton />}>
              <InviteRegisterPage />
            </Suspense>
          } />
          <Route path="/invite/:token" element={
            <Suspense fallback={<PageSkeleton />}>
              <InviteRegisterPage />
            </Suspense>
          } />
          {/* SPEC-426 — Stats surface preview path on any non-stats host (staging review). */}
          <Route path="/stats/*" element={
            <AuthGate>
              <Suspense fallback={<PageSkeleton />}>
                <StatsApp base="/stats" />
              </Suspense>
            </AuthGate>
          } />
          {/* Authenticated routes */}
          <Route path="*" element={
            <AuthGate>
              <ProductTypeProvider>
              <AppShell>
                <Suspense fallback={<PageSkeleton />}>
                  <Routes>
                    <Route path="/" element={<CommandCentrePage />} />
                    {/* SPEC-466 — Alerts ("What needs a look"): agent findings by remit */}
                    <Route path="/alerts" element={<AlertsPage />} />
                    <Route path="/intelligence" element={<IntelligencePage />} />
                    <Route path="/supply-chain" element={<SupplyChainPage />} />
                    <Route path="/demand-planning" element={<DemandPlanningPage />} />
                    {/* SPEC-230 — OOS horizon band pages (Dan's burn-down drilldowns) */}
                    <Route path="/oos/:weeks" element={<OosBandPage />} />
                    {/* SPEC-191 — Material Requirements (packaging + raw materials over the horizon) */}
                    <Route path="/material-requirements" element={<MaterialPlanningConsolePage />} />
                    {/* SPEC-399 — New-Product Matching (read-only analogue preview) */}
                    <Route path="/analogue-matching" element={<AnalogueMatchingPage />} />
                    {/* SPEC-403 — Production Upload (pre-MPS weekly build plan) */}
                    <Route path="/production-upload" element={<ProductionUploadPage />} />
                    {/* SPEC-406 — Stock Targets (read-only cover-band view) */}
                    <Route path="/inventory-targets" element={<InventoryTargetsPage />} />
                    {/* SPEC-407 — Forecast Benchmark (read-only: our forecast vs the ERP's buy report) */}
                    <Route path="/forecast-benchmark" element={<ForecastBenchmarkPage />} />
                    {/* SPEC-455 — Demand Source (read-only: which demand number is live) */}
                    <Route path="/demand-source" element={<DemandSourcePage />} />
                    <Route path="/recommendations" element={<RecommendationsPage />} />
                    {/* SPEC-434 — V7 promotional engine read layer (DARK preview) */}
                    <Route path="/box-a" element={<BoxACommandCentrePage />} />
                    <Route path="/forecast-workbench" element={<ForecastWorkbenchPage />} />
                    <Route path="/promotions" element={<PromotionsPage />} />
                    {/* SPEC-411 (scaffold) — Replenishment Signal Detail (stub) */}
                    <Route path="/replenishment-detail" element={<ReplenishmentDetailPage />} />
                    {/* SPEC-411 (scaffold) — Regime Alerts (stub) */}
                    <Route path="/regime-alerts" element={<RegimeAlertsPage />} />
                    <Route path="/supplier-risk" element={<SupplierRiskPage />} />
                    <Route path="/reports" element={<ReportsPage />} />
                    <Route path="/sales" element={<SalesPage />} />
                    {/* SPEC-411 (scaffold) — Customer Dormancy (stub) */}
                    <Route path="/customer-dormancy" element={<CustomerDormancyPage />} />
                    <Route path="/routes" element={<RoutesPage />} />
                    <Route path="/retro-billing" element={<RetroBillingPage />} />
                    {/* SPEC-417d — Deductions (promo participation live; reconciliation awaiting feeds) */}
                    <Route path="/deductions" element={<DeductionsPage />} />
                    <Route path="/governance" element={<GovernancePage />} />
                    {/* SPEC-448 — the method/policy/sign-off/audit half of the old Governance page */}
                    <Route path="/methodology" element={<MethodologyPage />} />
                    <Route path="/roadmap" element={<RoadmapPage />} />
                    {/* SPEC-503 — in-app living documentation. */}
                    <Route path="/docs" element={<DocsPage />} />
                    {/* SPEC-512 — one page per glossary term, nested under the Docs page. */}
                    <Route path="/docs/glossary" element={<Navigate to="/docs?page=ref-glossary" replace />} />
                    <Route path="/docs/glossary/:termId" element={<DocsPage />} />
                    <Route path="/products" element={<ProductsPage />} />
                    <Route path="/products/:code" element={<ProductDetailPage />} />
                    <Route path="/admin" element={<AdminPage />} />
                    <Route path="/super-admin" element={<SuperAdminPage />} />
                  </Routes>
                </Suspense>
              </AppShell>
              </ProductTypeProvider>
            </AuthGate>
          } />
          </>
          )}
        </Routes>
        <ToastContainer />
      </AuthProvider>
      </WorkspaceProvider>
    </BrowserRouter>
  );
}
